import { faImdb, faInstagram } from '@fortawesome/free-brands-svg-icons'
import { faPlay } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import DOMPurify from 'dompurify'
import { useEffect, useRef, useState } from 'react'
import ReactGA from 'react-ga4'
import GifPlayer from 'react-gif-player'
import { aboutGallery, aboutHeading } from '../data/about'
import { ImagePreloader } from './ImagePreloader'
import Masonry, { ResponsiveMasonry } from './Masonry'
import Modal from './Modal'

type GalleryItem = (typeof aboutGallery)[number]

function mulberry32(seed: number) {
  return function random() {
    let t = (seed += 0x6d2b79f5)
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function shuffleWithSeed<T>(items: T[], seed: number): T[] {
  const random = mulberry32(seed)
  const array = [...items]
  for (let i = array.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1))
    ;[array[i], array[j]] = [array[j], array[i]]
  }
  return array
}

export default function About() {
  const [playing, setPlaying] = useState(false)
  const [pauseGif, setPauseGif] = useState<null | (() => void)>(null)
  const [aboutExpanded, setAboutExpanded] = useState(false)
  const [galleryActive, setGalleryActive] = useState(false)
  const [visibleImageCount, setVisibleImageCount] = useState(12)
  const [selectedImage, setSelectedImage] = useState<null | GalleryItem>(null)
  const [randomizedGallery, setRandomizedGallery] = useState<GalleryItem[]>(
    () => [...aboutGallery]
  )
  const galleryRootRef = useRef<HTMLDivElement | null>(null)
  const gallerySeedRef = useRef<number | null>(null)

  useEffect(() => {
    const element = galleryRootRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          if (gallerySeedRef.current === null) {
            const seed = new Uint32Array(1)
            crypto.getRandomValues(seed)
            gallerySeedRef.current = seed[0]
          }

          setRandomizedGallery(
            shuffleWithSeed(aboutGallery, gallerySeedRef.current)
          )
          setVisibleImageCount(12)
          setGalleryActive(true)
          observer.disconnect()
        }
      },
      {
        root: null,
        rootMargin: '200px 0px',
        threshold: 0.01,
      }
    )

    observer.observe(element)
    return () => {
      observer.disconnect()
    }
  }, [])

  const className = 'z-0 h-auto w-full scale-100 rounded-xl'
  const modalClassName = 'h-auto w-full rounded-xl'
  return (
    <section
      id="about"
      className="container-responsive mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28"
    >
      <div className="grid grid-cols-2 gap-12 sm:grid-cols-8 md:grid-cols-8 lg:items-start">
        <div className="order-1 col-span-2 space-y-6 sm:col-span-5 md:col-span-5 lg:col-span-5">
          <div className="text-xs inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 font-medium uppercase tracking-[0.18em] text-neutral-700 dark:border-neutral-800 dark:bg-neutral-900/60 dark:text-neutral-300">
            <span>History · Backstory · Origin</span>
            <span className="h-px w-6 bg-neutral-400/70 dark:bg-neutral-600/80" />
          </div>
          <div className="prose prose-neutral dark:prose-invert max-w-none">
            <h2 className="sm:text-4xl mb-4 text-balance text-3xl font-semibold tracking-tight">
              {aboutHeading.heading ?? ''}
            </h2>
            <div
              className={`relative ${aboutExpanded ? '' : 'max-h-[220px] overflow-hidden sm:max-h-[360px] md:max-h-[440px] lg:max-h-[620px] xl:max-h-[600px]'}`}
            >
              <p
                // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(aboutHeading.paragraph ?? ''),
                }}
                className="prose-p"
              />
              {aboutExpanded ? null : (
                <div className="bg-linear-to-t pointer-events-none absolute bottom-0 left-0 h-12 w-full from-neutral-300 dark:from-gray-900" />
              )}
            </div>
            {(aboutHeading.paragraph ?? '').length > 0 ? (
              <button
                type="button"
                onClick={() => {
                  setAboutExpanded((expanded) => !expanded)
                  ReactGA.event({
                    category: 'Button Click',
                    action: 'User clicked to expand the About section',
                    label: 'About Section',
                  })
                }}
                className="text-sm mt-4 inline-flex items-center rounded-full border border-neutral-200 bg-neutral-50 px-4 py-2 font-semibold text-neutral-800 shadow-sm hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900/60 dark:text-neutral-200 dark:hover:bg-neutral-900"
              >
                {aboutExpanded ? 'Say less' : 'Say more'}
              </button>
            ) : null}
          </div>
        </div>
        <aside className="order-2 col-span-2 mx-auto h-full max-w-xs space-y-6 sm:col-span-3 sm:max-w-none md:col-span-3 lg:col-span-3">
          <div className="sticky top-16 overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-50/80 p-4 shadow-sm backdrop-blur-sm dark:border-neutral-800 dark:bg-neutral-900/60 sm:p-5">
            <div className="relative overflow-hidden rounded-xl bg-neutral-200/70 dark:bg-neutral-800/80">
              <GifPlayer
                key={playing ? 'playing' : 'paused'}
                gif="/media/andrew-halliwell-alifeinbinary.gif"
                still="/media/about/andrew-halliwell-alifeinbinary_320.webp"
                alt="Portrait of Andrew Halliwell, founder of Strange Ways Studio"
                className="h-full w-full object-cover object-center"
                autoplay={playing}
                onTogglePlay={setPlaying}
                pauseRef={(pause) => {
                  setPauseGif(() => pause)
                }}
                loading="lazy"
              />
              <button
                type="button"
                onClick={() => {
                  if (playing) {
                    pauseGif?.()
                    setPlaying(false)
                    ReactGA.event({
                      category: 'Button Click',
                      action: 'User clicked to pause the gif',
                      label: 'About Section',
                    })
                    return
                  }
                  setPlaying(true)
                  ReactGA.event({
                    category: 'Button Click',
                    action: 'User clicked to play the gif',
                    label: 'About Section',
                  })
                }}
                className={`z-100 absolute bottom-1/2 right-1/2 h-5 w-5 cursor-pointer text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-300 ${playing ? 'hidden' : ''}`}
                aria-label="Play gif animation"
              >
                <FontAwesomeIcon size="2xl" icon={faPlay} />
              </button>
            </div>

            <div className="mt-4 grid grid-cols-2 items-start justify-between gap-4">
              <div className="col-span-2 flex flex-col lg:col-span-1">
                <p className="text-sm font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
                  Andrew Halliwell
                </p>
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-neutral-700 dark:text-neutral-200">
                  Founder · Producer
                </p>
              </div>
              <p className="text-xs col-span-2 max-w-44 text-left text-neutral-700 dark:text-neutral-300 lg:col-span-1">
                Based in British Columbia, collaborating across time zones.
              </p>
            </div>
            <div className="mt-2 flex justify-start gap-2">
              <a
                type="button"
                className="h-10 w-10 hover:scale-110"
                href="https://instagram.com/alifeinbinary"
                aria-label="Andrew Halliwell's Instagram @alifeinbinary"
              >
                <FontAwesomeIcon size="2xl" icon={faInstagram} />
              </a>
              <a
                type="button"
                className="h-10 w-10 hover:scale-110"
                href="https://imdb.com/"
                aria-label="Andrew Halliwell's IMDB page"
              >
                <FontAwesomeIcon size="2xl" icon={faImdb} />
              </a>
            </div>
          </div>
        </aside>
      </div>

      <div className="mt-16 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-neutral-600 dark:text-neutral-300">
            Studio memories
          </h3>
          <p className="text-xs text-neutral-700 dark:text-neutral-200">
            Small fragments from projects, work-in-progress boards, and the
            studio itself.
          </p>
        </div>

        <div ref={galleryRootRef} className="gap-3">
          {galleryActive ? (
            <>
              <ResponsiveMasonry
                {...{
                  columnsCountBreakPoints: {
                    320: 3,
                    480: 4,
                    640: 5,
                    768: 6,
                    960: 6,
                    1200: 6,
                  },
                  className: 'relative z-20',
                  gutterBreakPoints: {
                    320: '1rem',
                    768: '1rem',
                    960: '1rem',
                  },
                }}
              >
                <Masonry>
                  {randomizedGallery
                    .slice(
                      0,
                      Math.min(visibleImageCount, randomizedGallery.length)
                    )
                    .map((image) => (
                      <div
                        key={image.id}
                        className="mb-3 break-inside-avoid overflow-hidden rounded-xl border border-neutral-200 bg-neutral-100/80 shadow-[0_6px_18px_rgba(15,23,42,0.08)] dark:border-neutral-800 dark:bg-neutral-900/60"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedImage(image)
                            ReactGA.event({
                              category: 'Button Click',
                              action: `User clicked the open image button: ${image.id}`,
                              label: 'About Section',
                            })
                          }}
                          className="block w-full cursor-zoom-in"
                          aria-label={`Open image: ${image.caption}`}
                        >
                          <picture>
                            <source
                              type="image/webp"
                              srcSet={`/media/about/${image.id}_320.webp 320w, /media/about/${image.id}_768.webp 768w, /media/about/${image.id}_960.webp 960w, /media/about/${image.id}_1200.webp 1200w`}
                              sizes="100vw"
                            />
                            <ImagePreloader
                              src={`/media/about/${image.id}_320.webp`}
                              alt={image.caption}
                              className={className}
                              loading="lazy"
                              decoding="async"
                            />
                          </picture>
                        </button>
                      </div>
                    ))}
                </Masonry>
              </ResponsiveMasonry>

              {visibleImageCount < randomizedGallery.length ? (
                <div className="mt-6 flex justify-center">
                  <button
                    type="button"
                    onClick={() => {
                      setVisibleImageCount((count) =>
                        Math.min(count + 6, randomizedGallery.length)
                      )
                      ReactGA.event({
                        category: 'Button Click',
                        action: 'User clicked to show more images',
                        label: 'About Section',
                      })
                    }}
                    className="text-sm inline-flex cursor-pointer items-center rounded-full border border-neutral-200 bg-neutral-50 px-5 py-2.5 font-semibold text-neutral-800 shadow-sm hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900/60 dark:text-neutral-200 dark:hover:bg-neutral-900"
                  >
                    show more
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      <Modal
        open={selectedImage !== null}
        onClose={(open) => {
          if (!open) setSelectedImage(null)
        }}
        title={selectedImage?.caption}
      >
        {selectedImage ? (
          <div className="w-full">
            <picture>
              <source
                type="image/webp"
                srcSet={`/media/about/${selectedImage.id}_768.webp 768w, /media/about/${selectedImage.id}_960.webp 960w, /media/about/${selectedImage.id}_1200.webp 1200w`}
                sizes="(min-width: 1024px) 1024px, 100vw"
              />
              <ImagePreloader
                src={`/media/about/${selectedImage.id}_1200.webp`}
                alt={selectedImage.caption}
                className={modalClassName}
                loading="eager"
                decoding="async"
              />
            </picture>
          </div>
        ) : null}
      </Modal>
    </section>
  )
}
